
import Vue from 'vue'
import Component from 'vue-class-component'

import MenuItem from '@/models/menuItem'
import Permissions from '@/models/constants/Permissions'

import { Action, State } from 'vuex-class'
import {User} from '../store/index'

@Component
export default class Navbar extends Vue {
  @State('user') private user!: User
  @State('isLogged') private isLogged!: boolean
  @Action private logout!: () => void

  // Using null as the starting value for its v-model will initialize the drawer as closed on mobile
  // and as open on desktop. (https://vuetifyjs.com/en/components/navigation-drawers)
  private overlay: boolean = false

  private get isMini() {
    return this.$vuetify.breakpoint.mdAndDown
  }

  private get isVisible() {
    return !this.isMini || this.overlay
  }

  private switchTheme() {
    if (this.$vuetify.theme.dark) {
      window.localStorage.setItem('MyEduDark', 'true')
    } else {
      window.localStorage.removeItem('MyEduDark')
    }
  }

  private logoutAction(): void {
    this.logout()
    this.$router.push('/login')
  }

  private get items(): MenuItem[] {
    const items: MenuItem[] = []

    items.push(
      new MenuItem(this.$t('MyAccount').toString(), 'mdi-card-account-details-outline', '/'),
    )
    if (this.user.schools.length !== 0) {
      items.push(
        new MenuItem(this.$t('MySchool').toString(), 'mdi-domain', '/my-school'),
      )
    }
    if (this.user.permissions) {
      if (this.hasAccessToCipeoView()) {
        items.push(
          new MenuItem(
            this.$t('CIPEOVIEW.menuTitle').toString(),
            'mdi-incognito',
            '/cipeo',
          ),
        )
      }
      if (this.hasAccessToUsers()) {
        items.push(
          new MenuItem(
            this.$t('USER.menuTitle').toString(),
            'mdi-account-group-outline',
            '/users',
          ),
        )
      }
      if (this.hasAccessToManageExtras()) {
        items.push(
          new MenuItem(
            this.$t('EXTRAS.menuTitle').toString(),
            'mdi-account-star-outline',
            '/extras',
          ),
        )
      }
      if (this.hasAccessToAccountVerification()) {
        items.push(
          new MenuItem(
            this.$t('NONNORMALIZEDACCOUNTS.menuTitle').toString(),
            'mdi-account-question-outline',
            '/account-verification',
          ),
        )
      }
      if (this.hasAccessToTempAccounts()) {
        items.push(
          new MenuItem(
            this.$t('TEMPACCOUNT.Title').toString(),
            'mdi-account-clock-outline',
            '/temporary-accounts',
          ),
        )
      }
      if (this.hasAccessToMireoAccounts()) {
        items.push(
          new MenuItem(
            this.$t('MIREOACCOUNT.Title').toString(),
            'mdi-account-switch-outline',
            '/substitutes',
          ),
        )
      }
      if (this.hasAccessToManageGuestUsers()) {
        items.push(
          new MenuItem(
            this.$t('GUESTS.Title').toString(),
            'mdi-account-tie-outline',
            '/guests',
          ),
        )
      }
      if (this.hasAccessToMassInsert()) {
        items.push(
          new MenuItem(
            this.$t('USERMASSINSERT.Title').toString(),
            'mdi-account-plus-outline',
            '/import-users-csv',
          ),
        )
      }
      if (this.hasAccessToEvents()) {
        items.push(
          new MenuItem(this.$t('EVENT.Title').toString(), 'history', '/events'),
        )
      }
      if (this.hasAccessToGroups()) {
        items.push(new MenuItem(this.$t('GROUPS.Title').toString(), 'mdi-database-cog-outline', '/groups'))
      }
      if (this.hasAccessToPermissions()) {
        items.push(new MenuItem(this.$t('PERMISSIONS.Title').toString(), 'mdi-lock-outline', '/permissions'))
      }
      if (this.hasAccessToRoles()) {
        items.push(new MenuItem(this.$t('ROLE.Title').toString(), 'mdi-account-cog-outline', '/roles'))
      }
      if (this.hasAccessToServices()) {
        items.push(new MenuItem(this.$t('SERVICES.Title').toString(), 'mdi-cloud-check-outline', '/services'))
      }
      if (this.hasAccessToAccessOpenApi()) {
        items.push(new MenuItem(this.$t('OPENAPI.Title').toString(), 'mdi-api', '/openapi'))
      }
    }
    return items
  }

  private hasAccessToCipeoView(): boolean {
    const allowed = [
      Permissions.AccessCipeoView,
    ]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToUsers(): boolean {
    const allowed = [
      Permissions.ListStudentAccounts,
      Permissions.ListSchoolStaffAccounts,
    ]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToMassInsert(): boolean {
    const allowed = [Permissions.BulkImportTempAccounts]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToMireoAccounts(): boolean {
    const allowed = [Permissions.ListMireoAccounts]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToManageGuestUsers(): boolean {
    const allowed = [Permissions.ManageGuestUsers]
    return (this.user.permissions as []).some((permission) => allowed.includes(permission))
  }

  private hasAccessToTempAccounts(): boolean {
    const allowed = [Permissions.ListTempAccounts]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToPermissions(): boolean {
    const allowed = [Permissions.ManageServices]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToRoles(): boolean {
    const allowed = [Permissions.ManageRoles, Permissions.ManageSchoolRoles]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToServices(): boolean {
    const allowed = [Permissions.ManageServices]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToEvents(): boolean {
    const allowed = [
      Permissions.ListAllEvents,
      Permissions.ListSchoolsEvents,
      Permissions.ListUserEvents,
    ]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToAccountVerification(): boolean {
    const allowed = [
      Permissions.ListNonNormalizedAccounts,
      Permissions.ListWaitingForSyncAccounts,
    ]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToAccessOpenApi(): boolean {
    const allowed = [Permissions.AccessOpenAPI]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private hasAccessToManageExtras(): boolean {
    const allowed = [Permissions.ManageExtras]
    return (this.user.permissions as []).some((permission) => allowed.includes(permission))
  }

  private hasAccessToGroups(): boolean {
    const allowed = [Permissions.ManageAdGroups]
    return (this.user.permissions as []).some((permission) => allowed.includes(permission))
  }
}
